import React, { useEffect, useRef, useState } from 'react';
import { func, string } from 'prop-types';
import classNames from 'classnames';

import css from './KeywordAutocompleteInput.module.css';

// Search-as-you-type for the `/s` keyword box (docs/search-v2-s-autocomplete-ai.md). A controlled
// text input with a suggestion listbox: typing never touches the URL; only selecting a suggestion
// (`onSelect`) or submitting (`onEnter`, or the surrounding form) commits a search.
export const SUGGEST_DEBOUNCE_MS = 150;
export const SUGGEST_MIN_CHARS = 2;
export const SUGGEST_MAX = 8;

const preventDefault = e => e.preventDefault();

const KeywordAutocompleteInput = props => {
  const {
    id,
    value,
    onChange,
    onEnter,
    onSelect,
    fetchSuggestions,
    className,
    inputClassName,
    placeholder,
    ariaLabel,
  } = props;
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef(null);
  // Only the newest request may update the list: every keystroke, close and selection bumps it.
  const latestRequest = useRef(0);
  const abortRef = useRef(null);
  const timerRef = useRef(null);
  // `value` also changes when the URL changes; only typing fetches suggestions
  const typedRef = useRef(false);
  const focusedRef = useRef(false);
  const fetchRef = useRef(fetchSuggestions);
  fetchRef.current = fetchSuggestions;
  const listId = `${id}-suggestions`;

  const invalidate = () => {
    latestRequest.current += 1;
    clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
  };

  const close = () => {
    invalidate();
    setOpen(false);
    setActive(-1);
  };

  useEffect(() => invalidate, []);

  useEffect(() => {
    if (!typedRef.current) return;
    typedRef.current = false;
    invalidate();
    const query = (value || '').trim();
    if (query.length < SUGGEST_MIN_CHARS) {
      setSuggestions([]);
      setOpen(false);
      setActive(-1);
      return;
    }
    const requestId = latestRequest.current;
    timerRef.current = setTimeout(() => {
      const controller = typeof AbortController === 'undefined' ? null : new AbortController();
      abortRef.current = controller;
      Promise.resolve(fetchRef.current(query, controller ? { signal: controller.signal } : {}))
        .then(list => {
          if (requestId !== latestRequest.current || !focusedRef.current) return;
          const next = Array.isArray(list) ? list.slice(0, SUGGEST_MAX) : [];
          setSuggestions(next);
          setActive(-1);
          setOpen(next.length > 0);
        })
        .catch(() => {
          if (requestId !== latestRequest.current) return;
          setSuggestions([]);
          setOpen(false);
          setActive(-1);
        });
    }, SUGGEST_DEBOUNCE_MS);
  }, [value]);

  const select = suggestion => {
    close();
    if (inputRef.current) inputRef.current.blur();
    onSelect(suggestion);
  };

  const handleKeyDown = e => {
    const n = open ? suggestions.length : 0;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!n) return;
      e.preventDefault();
      // read now: React 16 pools the event, so the updater must not touch `e`
      const down = e.key === 'ArrowDown';
      // -1 is the typed text itself
      setActive(a => (down ? (a + 1 >= n ? -1 : a + 1) : a - 1 < -1 ? n - 1 : a - 1));
    } else if (e.key === 'Enter') {
      if (n && active >= 0) {
        e.preventDefault();
        e.stopPropagation();
        select(suggestions[active]);
        return;
      }
      close();
      // without onEnter the surrounding form submits as before
      if (onEnter) onEnter();
    } else if (e.key === 'Escape') {
      if (n) e.preventDefault();
      // also when nothing is shown yet: the pending request must not reopen the list
      close();
    } else if (e.key === 'Tab') {
      close();
    }
  };

  const isOpen = open && suggestions.length > 0;
  return (
    <div className={classNames(css.root, className)}>
      <input
        ref={inputRef}
        id={id}
        className={inputClassName}
        type="text"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-label={ariaLabel}
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-activedescendant={isOpen && active >= 0 ? `${listId}-${active}` : undefined}
        onChange={e => {
          typedRef.current = true;
          // the list belongs to the previous text: never selectable once the text changes
          setSuggestions([]);
          close();
          onChange(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          focusedRef.current = true;
        }}
        onBlur={() => {
          focusedRef.current = false;
          close();
        }}
      />
      <ul id={listId} role="listbox" className={css.listbox} hidden={!isOpen}>
        {isOpen
          ? suggestions.map((s, i) => (
              <li
                key={`${s.type}:${s.text}:${i}`}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                className={classNames(css.option, { [css.optionActive]: i === active })}
                // keep the input focused until the click selects
                onMouseDown={preventDefault}
                onMouseEnter={() => setActive(i)}
                onClick={() => select(s)}
              >
                <span className={css.optionText}>{s.text}</span>
                {s.secondary ? <span className={css.optionSecondary}>{s.secondary}</span> : null}
              </li>
            ))
          : null}
      </ul>
    </div>
  );
};

KeywordAutocompleteInput.defaultProps = {
  onEnter: null,
  className: null,
  inputClassName: null,
  placeholder: null,
  ariaLabel: null,
};

KeywordAutocompleteInput.propTypes = {
  id: string.isRequired,
  value: string.isRequired,
  onChange: func.isRequired,
  onEnter: func,
  onSelect: func.isRequired,
  fetchSuggestions: func.isRequired,
  className: string,
  inputClassName: string,
  placeholder: string,
  ariaLabel: string,
};

export default KeywordAutocompleteInput;
